import { Suspense, type ReactNode } from "react";
import { Navigate, useLocation, type RouteObject } from "react-router-dom";
import ProtectedRoute from "../../components/auth/ProtectedRoute";
import PageLoader from "../../components/common/PageLoader";
import ErrorBoundary from "../../features/shared/pages/ErrorBoundary";
import MainLayout from "../../layouts/MainLayout";

const renderProtectedLayout = () => (
  <ProtectedRoute>
    <MainLayout />
  </ProtectedRoute>
);

export const withSuspense = (element: ReactNode, fallback: ReactNode = <PageLoader />) => (
  <Suspense fallback={fallback}>{element}</Suspense>
);

export const createProtectedIndexRoute = (
  path: string,
  element: ReactNode,
  options: Partial<RouteObject> = {}
): RouteObject => ({
  path,
  element: renderProtectedLayout(),
  errorElement: <ErrorBoundary />,
  children: [{ index: true, element }],
  ...options,
});

export const createProtectedStandaloneRoute = (
  path: string,
  element: ReactNode,
  options: Partial<RouteObject> = {}
): RouteObject => ({
  path,
  element: <ProtectedRoute>{element}</ProtectedRoute>,
  errorElement: <ErrorBoundary />,
  ...options,
});

/**
 * Redirect that carries the incoming query string along.
 *
 * <p>A plain {@code <Navigate to="/x">} drops the query, which breaks legacy
 * links that pass state that way — {@code /BillMaster?Push=1} must arrive at
 * {@code /bills/new?Push=1}, or the push-from-purchase-order flow lands on a
 * blank form and silently does nothing.
 */
export const RedirectWithQuery = ({ to }: { to: string }) => {
  const location = useLocation();
  // Must be a single string. The router shim only parses a string `to`; an
  // object is handed to TanStack untouched and ends up stringified into the
  // path as "/BillMaster/[object Object]".
  return <Navigate replace to={`${to}${location.search ?? ''}`} />;
};

export const createProtectedRedirectRoute = (
  path: string,
  to: string,
  options: Partial<RouteObject> = {}
): RouteObject => createProtectedStandaloneRoute(path, <Navigate replace to={to} />, options);
